import React, { useState } from "react";
import { useAuth } from "@/App";
import { LayoutGrid, Wand2, Gauge, Layers, Palette, LogOut, CreditCard, Coins, History } from "lucide-react";
import GenerateTab from "@/components/tabs/GenerateTab";
import OptimizeTab from "@/components/tabs/OptimizeTab";
import GraderTab from "@/components/tabs/GraderTab";
import BulkTab from "@/components/tabs/BulkTab";
import BrandVoiceTab from "@/components/tabs/BrandVoiceTab";
import HistoryTab from "@/components/tabs/HistoryTab";
import CreditsPanel from "@/components/CreditsPanel";

const TABS = [
  { id: "generate", label: "Generate", icon: Wand2 },
  { id: "optimize", label: "Optimize", icon: LayoutGrid },
  { id: "grader",   label: "Grader",   icon: Gauge },
  { id: "bulk",     label: "Bulk",     icon: Layers },
  { id: "brand-voice", label: "Brand voice", icon: Palette },
  { id: "history",  label: "History",  icon: History },
  { id: "credits",  label: "Credits",  icon: CreditCard },
];

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const initial = (() => {
    const p = new URLSearchParams(window.location.search);
    return TABS.find(t => t.id === p.get("tab"))?.id || "generate";
  })();
  const [tab, setTab] = useState(initial);
  const [prefill, setPrefill] = useState(null);

  const handleRerun = (input) => {
    setPrefill({ ...input, _t: Date.now() });
    setTab("generate");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="dash-brand">
            <span className="inline-block w-7 h-7 rounded-md bg-lime" />
            <span className="font-black h-tight">TagTitan</span>
          </a>
          <div className="flex items-center gap-3">
            <div data-testid="credits-badge" className="hidden sm:flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-1.5 text-sm">
              <Coins size={14} className="text-zinc-500" />
              {user?.quota_left > 0 ? (
                <><span className="font-bold mono">{user.quota_left}</span><span className="text-zinc-500">left this month</span></>
              ) : (
                <><span className="font-bold mono">{user?.credits ?? 0}</span><span className="text-zinc-500">credits</span></>
              )}
            </div>
            <div className="hidden sm:flex items-center gap-2 text-sm">
              {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
              <span className="font-medium">{user?.name || user?.email}</span>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="btn-ghost inline-flex items-center gap-1 text-sm">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8 grid lg:grid-cols-[220px,1fr] gap-8">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={"flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors " +
                    (active ? "bg-[#09090B] text-white" : "text-zinc-700 hover:bg-zinc-100")}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main data-testid="dashboard-main">
          {tab === "generate" && <GenerateTab onRun={refresh} prefill={prefill} />}
          {tab === "optimize" && <OptimizeTab onRun={refresh} />}
          {tab === "grader"   && <GraderTab />}
          {tab === "bulk"     && <BulkTab onRun={refresh} />}
          {tab === "brand-voice" && <BrandVoiceTab />}
          {tab === "history"  && <HistoryTab onRerun={handleRerun} />}
          {tab === "credits"  && <CreditsPanel />}
        </main>
      </div>
    </div>
  );
}
